<template>
  <v-dialog
    v-model="dialog"
    persistent
    scrollable
    :fullscreen="$vuetify.display.xs"
    :width="$vuetify.display.smAndUp ? 600 : 'auto'"
    style="z-index: 1100 !important;"
  >
    <v-form
      ref="form"
      @submit.prevent="updateClient"
      autocomplete="off"
      data-cy="dialog-editClient-form"
    >
      <v-card v-cloak>
        <v-card-title class="d-flex justify-space-between" data-cy="dialog-editClient-title">
          {{ $t('routes.apiClients.editClient') }}

          <v-btn icon variant="text" @click="dialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text scrollable class="pb-0 pt-4 scrollable">
          <v-text-field
            v-model="name"
            :label="$t('routes.apiClients.clientName')"
            :rules="nameRules"
            autocomplete="off"
            required
            variant="solo-filled"
            flat
            data-cy="dialog-editClient-name"
          />

          <!-- Scopes Selection -->
          <div class="mb-4">
            <div class="text-subtitle-2 mb-2">{{ $t('routes.apiClients.scopes') }} *</div>

            <!-- Files Scopes -->
            <div class="text-caption text-medium-emphasis mb-1">{{ $t('routes.apiClients.scopeFiles') }}</div>
            <div class="d-flex align-center flex-wrap mb-2">
              <v-switch
                v-model="filesFullAccess"
                :label="filesFullAccess ? $t('routes.apiClients.scopeFullAccess') : $t('routes.apiClients.scopeIndividual')"
                density="compact"
                hide-details
                color="primary"
                class="mr-4"
                data-cy="scope-files-switch"
              />
              <template v-if="!filesFullAccess">
                <v-checkbox
                  v-model="filesRead"
                  :label="$t('routes.apiClients.scopeRead')"
                  density="compact"
                  hide-details
                  class="mr-4"
                  data-cy="scope-files-read"
                />
                <v-checkbox
                  v-model="filesWrite"
                  :label="$t('routes.apiClients.scopeWrite')"
                  density="compact"
                  hide-details
                  data-cy="scope-files-write"
                />
              </template>
            </div>

            <!-- Folders Scopes -->
            <div class="text-caption text-medium-emphasis mb-1">{{ $t('routes.apiClients.scopeFolders') }}</div>
            <div class="d-flex align-center flex-wrap mb-2">
              <v-switch
                v-model="foldersFullAccess"
                :label="foldersFullAccess ? $t('routes.apiClients.scopeFullAccess') : $t('routes.apiClients.scopeIndividual')"
                density="compact"
                hide-details
                color="primary"
                class="mr-4"
                data-cy="scope-folders-switch"
              />
              <template v-if="!foldersFullAccess">
                <v-checkbox
                  v-model="foldersRead"
                  :label="$t('routes.apiClients.scopeRead')"
                  density="compact"
                  hide-details
                  class="mr-4"
                  data-cy="scope-folders-read"
                />
                <v-checkbox
                  v-model="foldersWrite"
                  :label="$t('routes.apiClients.scopeWrite')"
                  density="compact"
                  hide-details
                  data-cy="scope-folders-write"
                />
              </template>
            </div>

            <!-- Links Scopes -->
            <div class="text-caption text-medium-emphasis mb-1">{{ $t('routes.apiClients.scopeLinks') }}</div>
            <div class="d-flex align-center flex-wrap mb-2">
              <v-switch
                v-model="linksFullAccess"
                :label="linksFullAccess ? $t('routes.apiClients.scopeFullAccess') : $t('routes.apiClients.scopeIndividual')"
                density="compact"
                hide-details
                color="primary"
                class="mr-4"
                data-cy="scope-links-switch"
              />
              <template v-if="!linksFullAccess">
                <v-checkbox
                  v-model="linksRead"
                  :label="$t('routes.apiClients.scopeRead')"
                  density="compact"
                  hide-details
                  class="mr-4"
                  data-cy="scope-links-read"
                />
                <v-checkbox
                  v-model="linksWrite"
                  :label="$t('routes.apiClients.scopeWrite')"
                  density="compact"
                  hide-details
                  data-cy="scope-links-write"
                />
              </template>
            </div>

            <div v-if="scopeError" class="text-error text-caption">
              {{ $t('routes.apiClients.scopesRequired') }}
            </div>
          </div>

          <div class="bg-error" v-if="errorMessage">
            {{ errorMessage }}
          </div>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>

          <v-btn
            type="submit"
            class="bg-primary"
            :disabled="isSubmitDisabled || isFormSubmitted"
            elevation="0"
            data-cy="dialog-editClient-submit-btn"
          >
            {{ $t('routes.apiClients.save') }}
          </v-btn>
          <v-btn
            variant="text"
            :disabled="isFormSubmitted"
            @click="dialog = false"
            data-cy="dialog-editClient-cancel"
          >
            {{ $t('common.cancel') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-form>
  </v-dialog>
</template>

<script>
import { useApiClientStore } from '@/stores/ApiClientStore.js'
import DialogActionKeys from '@/components/mixin/DialogActionKeys.js';

export default {
  mixins: [DialogActionKeys],
  name: "EditClientDialog",
  props: {
    client: {
      type: Object,
      required: true,
    },
    accessPointId: {
      type: String,
      required: true,
    },
    modelValue: {
      type: Boolean,
      required: true,
    },
  },
  emits: ['clientUpdated', 'update:modelValue'],
  data() {
    return {
      errorMessage: '',

      name: '',
      nameRules: [
        v => !!v || this.$t('routes.apiClients.nameRequired'),
        v => (v && v.length <= 255) || this.$t('routes.apiClients.nameTooLong'),
      ],

      // Scope switches: true = Full Access, false = Individual permissions
      filesFullAccess: true,
      foldersFullAccess: true,
      linksFullAccess: true,

      // Individual permission selections (used when fullAccess = false)
      filesRead: false,
      filesWrite: false,
      foldersRead: false,
      foldersWrite: false,
      linksRead: false,
      linksWrite: false,

      scopeError: false,

      isFormSubmitted: false,
    };
  },
  watch: {
    dialog(newValue) {
      if (newValue) {
        this.initializeForm();
      }
    },
  },
  mounted() {
    this.initializeForm();
  },
  methods: {
    initializeForm() {
      if (this.$refs.form) {
        this.$refs.form.resetValidation();
      }
      this.errorMessage = '';
      this.scopeError = false;
      this.isFormSubmitted = false;

      // Pre-fill name
      this.name = this.client.name || '';

      // Parse and set scopes
      const scopes = this.client.scopes || [];

      // Files scopes
      if (scopes.includes('files:full')) {
        this.filesFullAccess = true;
        this.filesRead = false;
        this.filesWrite = false;
      } else {
        this.filesFullAccess = false;
        this.filesRead = scopes.includes('files:read');
        this.filesWrite = scopes.includes('files:write');
      }

      // Folders scopes
      if (scopes.includes('folders:full')) {
        this.foldersFullAccess = true;
        this.foldersRead = false;
        this.foldersWrite = false;
      } else {
        this.foldersFullAccess = false;
        this.foldersRead = scopes.includes('folders:read');
        this.foldersWrite = scopes.includes('folders:write');
      }

      // Links scopes
      if (scopes.includes('links:full')) {
        this.linksFullAccess = true;
        this.linksRead = false;
        this.linksWrite = false;
      } else {
        this.linksFullAccess = false;
        this.linksRead = scopes.includes('links:read');
        this.linksWrite = scopes.includes('links:write');
      }
    },
    async updateClient() {
      if (this.isFormSubmitted) {
        return;
      }

      this.errorMessage = '';
      this.scopeError = false;

      const { valid: validForm } = await this.$refs.form.validate();
      if (!validForm) {
        return;
      }

      // Validate scopes (at least one must be selected)
      if (this.selectedScopes.length === 0) {
        this.scopeError = true;
        return;
      }

      this.isFormSubmitted = true;

      try {
        const apiClientStore = useApiClientStore();
        const updatedClient = await apiClientStore.updateClient(
          this.client.id,
          {
            name: this.name,
            scopes: this.selectedScopes,
          },
          this.accessPointId
        );

        this.dialog = false;
        this.$emit('clientUpdated', updatedClient);
      } catch (error) {
        if (error?.response?.data?.error) {
          const apiError = error.response.data.error;
          this.errorMessage = apiError.message || this.$t('routes.apiClients.updateError');
        } else {
          this.errorMessage = this.$t('routes.apiClients.updateError');
          console.error(error);
        }
      } finally {
        this.isFormSubmitted = false;
      }
    },
  },
  computed: {
    dialog: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit('update:modelValue', value);
      },
    },
    selectedScopes() {
      const scopes = [];

      // Files scopes
      if (this.filesFullAccess) {
        scopes.push('files:full');
      } else {
        if (this.filesRead) scopes.push('files:read');
        if (this.filesWrite) scopes.push('files:write');
      }

      // Folders scopes
      if (this.foldersFullAccess) {
        scopes.push('folders:full');
      } else {
        if (this.foldersRead) scopes.push('folders:read');
        if (this.foldersWrite) scopes.push('folders:write');
      }

      if (this.linksFullAccess) {
        scopes.push('links:full');
      } else {
        if (this.linksRead) scopes.push('links:read');
        if (this.linksWrite) scopes.push('links:write');
      }

      return scopes;
    },
    isSubmitDisabled() {
      return this.selectedScopes.length === 0;
    },
  },
}
</script>
